import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { AlertCircle, ArrowUp, FileText, Loader2, Mic, Plus, Smile, Square, X } from 'lucide-react'
import { cn } from 'lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover'
import MediaPickerSheet from 'features/Chat/components/ChatInput/MediaPickerSheet'
import useAudioRecorder from 'features/Chat/hooks/useAudioRecorder'

// The emoji dataset is ~1MB and most sessions never open the picker, so it is
// fetched on first use rather than bundled into the app entry.
const EmojiPicker = lazy(() => import('features/Chat/components/ChatInput/EmojiPicker'))

export interface Props {
	value: string
	isDisabled?: boolean
	pendingAttachments?: any[]
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
	onSend: (e: React.FormEvent | React.MouseEvent) => void
	onInsertEmoji?: (native: string) => void
	onAddAttachments?: (files: File[]) => void
	onRemoveAttachment?: (tempId: string) => void
	/** Called on each keystroke so the thread can announce typing. */
	onTyping?: () => void
}

const MAX_TEXTAREA_PX = 100

/** iOS-style composer: media button, rounded pill, circular send. */
const Composer = ({
	value,
	isDisabled = false,
	pendingAttachments = [],
	onChange,
	onSend,
	onInsertEmoji,
	onAddAttachments,
	onRemoveAttachment,
	onTyping,
}: Props) => {
	const [emojiOpen, setEmojiOpen] = useState(false)
	const [mediaOpen, setMediaOpen] = useState(false)

	/*
	 * A voice message is just another attachment once it exists, so the finished
	 * recording goes through the same onAddAttachments path as a photo — upload,
	 * pending row, retry — rather than growing a second send path of its own.
	 */
	const recorder = useAudioRecorder({
		onComplete: file => onAddAttachments?.([file]),
	})
	const isRecording = recorder.status === 'recording' || recorder.status === 'requesting'
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	// Enter sends on a physical keyboard; on touch it must insert a newline,
	// because there is no other way to get one.
	const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches

	// Autogrow. Reset to auto first or the box can only ever get taller.
	useEffect(() => {
		const el = textareaRef.current
		if (!el) return
		el.style.height = 'auto'
		el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_PX)}px`
	}, [value])

	const hasReadyAttachment = pendingAttachments.some(a => a.url && !a.uploading && !a.error)
	const canSend = !isDisabled && (!!value.trim() || hasReadyAttachment)

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key !== 'Enter' || e.shiftKey || isTouch) return
		e.preventDefault()
		if (canSend) onSend(e)
	}

	return (
		<div
			data-testid="chat-popup-composer"
			className="shrink-0 border-t border-gray-200 bg-white px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
		>
			{pendingAttachments.length > 0 && (
				<div className="mb-2 flex flex-wrap gap-2">
					{pendingAttachments.map(att => (
						<div
							key={att.tempId}
							className="relative h-16 w-16 overflow-hidden rounded-md border border-gray-200 bg-gray-50"
							title={att.error ? `${att.name}: ${att.error}` : att.name}
						>
							{att.previewUrl || (att.kind === 'image' && att.url) ? (
								<img
									src={att.previewUrl || att.url}
									alt={att.name}
									className="h-full w-full object-cover"
								/>
							) : (
								<div className="flex h-full w-full flex-col items-center justify-center gap-1 px-1">
									<FileText className="h-5 w-5 text-gray-400" />
									<span className="w-full truncate text-center text-[10px] leading-tight text-gray-500">
										{att.name}
									</span>
								</div>
							)}

							{att.uploading && (
								<div className="absolute inset-0 flex items-center justify-center bg-white/60">
									<Loader2 className="h-5 w-5 animate-spin text-[#0080fe]" />
								</div>
							)}
							{att.error && (
								<div className="absolute inset-0 flex items-center justify-center bg-red-50/80">
									<AlertCircle className="h-5 w-5 text-red-500" />
								</div>
							)}

							<button
								type="button"
								aria-label={`Remove ${att.name}`}
								onClick={() => onRemoveAttachment?.(att.tempId)}
								className="absolute right-0.5 top-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
							>
								<X className="h-3 w-3" />
							</button>
						</div>
					))}
				</div>
			)}

			{recorder.error && (
				<p role="alert" className="mb-2 px-1 text-xs text-destructive">
					{recorder.error}
				</p>
			)}

			<form className="flex items-end gap-2" onSubmit={onSend}>
				{isRecording ? (
					<>
						<button
							type="button"
							onClick={recorder.cancel}
							aria-label="Cancel recording"
							className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
						>
							<X className="h-5 w-5" />
						</button>

						<div className="flex min-w-0 flex-1 items-center gap-2 rounded-[18px] border border-gray-300 bg-white px-3 py-2">
							<span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-destructive" />
							<span className="text-sm text-gray-600">
								{recorder.status === 'requesting' ? 'Waiting for the mic…' : 'Recording'}
							</span>
							<span className="ml-auto text-sm tabular-nums text-gray-500">
								{Math.floor(recorder.seconds / 60)}:
								{String(recorder.seconds % 60).padStart(2, '0')}
							</span>
						</div>

						<button
							type="button"
							onClick={recorder.stop}
							aria-label="Finish recording"
							className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#0080fe] text-white"
						>
							<Square className="h-4 w-4 fill-current" />
						</button>
					</>
				) : (
				<>
				{onAddAttachments && (
					<Popover open={mediaOpen} onOpenChange={setMediaOpen}>
						<PopoverTrigger asChild>
							<button
								type="button"
								aria-label="Add media"
								disabled={isDisabled}
								className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-50"
							>
								<Plus className="h-5 w-5" />
							</button>
						</PopoverTrigger>
						{/* Radix portals with its own stacking context, well below the
						    popup shell's z-index, so it needs an explicit one. */}
						<PopoverContent
							side="top"
							align="start"
							className="z-[10004] w-auto border-none bg-transparent p-0 shadow-none"
						>
							<MediaPickerSheet onFiles={onAddAttachments} onClose={() => setMediaOpen(false)} />
						</PopoverContent>
					</Popover>
				)}

				{/* Hidden where the browser cannot record at all, rather than
				    offered and failing on tap. */}
				{onAddAttachments && recorder.supported && !value.trim() && (
					<button
						type="button"
						onClick={recorder.start}
						aria-label="Record a voice message"
						disabled={isDisabled}
						className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-50"
					>
						<Mic className="h-5 w-5" />
					</button>
				)}

				<div className="flex min-w-0 flex-1 items-end rounded-[18px] border border-gray-300 bg-white px-3 py-1.5">
					<textarea
						ref={textareaRef}
						rows={1}
						value={value}
						onChange={e => {
							onChange(e)
							onTyping?.()
						}}
						onKeyDown={handleKeyDown}
						disabled={isDisabled}
						placeholder="Message"
						aria-label="Message"
						className="min-w-0 flex-1 resize-none bg-transparent text-[15px] leading-[20px] outline-none placeholder:text-gray-400 disabled:opacity-50"
						style={{ maxHeight: MAX_TEXTAREA_PX }}
					/>
					{onInsertEmoji && (
						<Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
							<PopoverTrigger asChild>
								<button
									type="button"
									aria-label="Insert emoji"
									disabled={isDisabled}
									className="mb-0.5 ml-1 shrink-0 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
								>
									<Smile className="h-5 w-5" />
								</button>
							</PopoverTrigger>
							<PopoverContent
								side="top"
								align="end"
								className="z-[10004] w-auto border-none bg-transparent p-0 shadow-none"
								// Without this the trigger reclaims focus on close and
								// the caret jumps to the end, undoing the insert.
								onCloseAutoFocus={e => e.preventDefault()}
							>
								<Suspense
									fallback={
										<div className="flex h-[435px] w-[352px] items-center justify-center rounded-lg border border-gray-200 bg-white text-sm text-gray-400">
											Loading emoji…
										</div>
									}
								>
									<EmojiPicker
										onSelect={(native: string) => {
											onInsertEmoji(native)
											setEmojiOpen(false)
										}}
									/>
								</Suspense>
							</PopoverContent>
						</Popover>
					)}
				</div>

				<button
					type="submit"
					aria-label="Send message"
					disabled={!canSend}
					className={cn(
						'grid h-8 w-8 shrink-0 place-items-center rounded-full text-white transition-all',
						canSend ? 'bg-[#0080fe]' : 'scale-90 bg-gray-300',
					)}
				>
					<ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.5} />
				</button>
				</>
				)}
			</form>
		</div>
	)
}

export default Composer
